/**
 * @description 隐私政策组件
 * @author Cursor
 * @date 2026-02-03
 */
import React, { useState, useEffect } from 'react';
import { Card } from 'antd';
import axios from 'axios';
import { getLocale } from 'src/gs-utils/core/storage/LocaleManager';
import { generateConfig } from 'src/gs-utils/core/utils';
import { API_URL_CONFIG_TYPE } from 'src/gs-utils/core/types';
import { getPrivacyDomainRegion } from 'src/gs-utils/core/config/domain';
import MarkDownRender from './MarkDownRender';

/**
 * 隐私政策版本信息接口响应类型 - write by cursor
 */
interface IPrivacyAgreementResponse {
  urls: Record<string, string>;
  version: string;
}

/**
 * API 响应包装类型 - write by cursor
 */
interface IApiResponse<T> {
  data?: T;
  code?: number;
  msg?: string;
}

/**
 * 隐私政策组件 - write by cursor
 */
const PrivacyAgreement: React.FC = () => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * 获取隐私政策版本信息 - write by cursor
   */
  const getPrivacyAgreementInfo = async (): Promise<IPrivacyAgreementResponse> => {
    try {
      setLoading(true);
      const { bot } = generateConfig() as API_URL_CONFIG_TYPE;
      const region = getPrivacyDomainRegion();
      const url = `${bot}/gas/api/v1alpha1/user/privacy-agreement/versions/latest${region ? `?region=${region}` : ''}`;

      // 使用 axios.get 直接调用接口，参考 token-manager.ts 的写法 - write by cursor
      const response = await axios.get<IApiResponse<IPrivacyAgreementResponse>>(url, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // 参考 token-manager.ts 的写法，直接访问 response.data?.data - write by cursor
      const data = response.data?.data || (response.data as any);
      if (data && data.urls) {
        return Promise.resolve(data);
      } else {
        return Promise.reject(new Error('Invalid response'));
      }
    } catch (err: any) {
      const { data } = err.response || {};
      const { error } = data || {};
      return Promise.reject(error || err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 获取隐私政策 URL - write by cursor
   */
  const getInfo = () => {
    getPrivacyAgreementInfo()
      .then((res) => {
        const { urls } = res;
        const locale = getLocale();
        if (locale === 'cn' || locale === 'zh-CN') {
          setUrl(urls['zh-CN']);
        } else if (locale === 'en' || locale === 'en-US') {
          setUrl(urls['en-US']);
        } else {
          // 上两个分支特殊处理，后续新增的语言，均走标准的语言映射，未找到映射时英文兜底 - write by cursor
          setUrl(urls[locale] || urls['en-US']);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch privacy agreement:', err);
      });
  };

  useEffect(() => {
    getInfo();
  }, []);

  return <div style={{ margin: 24 }} >
    <Card>
      <MarkDownRender url={url} loading={loading} />
    </Card>
  </div>;
};
export default PrivacyAgreement;
